/**
 * 场景二：购物车页面 - 合并 GraphQL 查询定义
 *
 * 将原本 7 个串行请求合并为 3 步串行：
 * - Step 1: shoppingCartSettlement (Mutation，保持不变)
 * - Step 2: CartInit (2 合 1：getShoppingCart + activityCountdownByCart)
 * - Step 3: CartExtras (6 合 1：orderPromotionResult + checkProductCanCheckout + freeGiftByOrder + markUpByOrder + getActiveCoupons + getOrderIsIncludeMemberPrice)
 */
import { PURCHASE_PREMIUM_FRAGMENT, USER_COUPON_FRAGMENT } from '@/services/graphql/fragments';
import gql from 'graphql-tag';

/** 购物车订单行字段 - 与 placeOrder.ts 中保持一致 */
const shoppingCartOrderLineFields = `
  fragment OrderLine on OrderLine {
    id
    quantity
    unitPrice
    productVariant {
      ...ProductVariant
    }
    customFields {
      purchasePattern
    }
  }

  fragment ProductVariant on ProductVariant {
    id
    sku
    price
    name
    exclusionGroupsTypeName
    isCanPurchase
    options {
      code
      name
    }
    pointExchange {
      points
    }
    featuredAsset {
      source
    }
    isThereAnyStock
    product {
      ...Product
    }
    customFields {
      virtualTargetType
    }
  }

  fragment Product on Product {
    id
    name
    enabled
    customFields {
      putOnSaleType
      putOnSaleTime
    }
    productTotalStock
    productPurchasePermission {
      id
      productId
      isMembershipPlanPurchase
      membershipPlans {
        id
      }
      guideMembershipPlanId
    }
    memberPriceActivityAmount {
      minDiscount
      maxDiscount
      minMemberPriceAmount
      maxMemberPriceAmount
      memberPriceProductVariant {
        memberPriceAmount
        productVariantId
        memberDiscount
        discountType
      }
    }
    featuredAsset {
      source
    }
    customFields {
      price
      markingPrice
      limitType
      limitCount
      putOnSaleType
      putOnSaleTime
      isVipProduct
      isHiddenCart
      virtualTargetType
    }
    pointsExchangeInfo {
      allowPurchaseAtOriginalPrice
    }
  }
`;

/** 购物车订单基础字段 - 与 placeOrder.ts 中保持一致 */
const shoppingCartOrderBaseFields = `
  id
  shipping
  customFields {
    orderPromotionResult {
      promResult {
        orderTotalPrice
      }
    }
  }
  lines {
    ...OrderLine
  }
`;

/**
 * Step 2: CartInit - 购物车核心数据（2 合 1）
 * 合并: getShoppingCart + activityCountdownByCart
 *
 * 包含自提信息版本
 */
export const CART_INIT_WITH_PICKUP_QUERY = gql`
  query CartInit(
    $isRemoveMarkUp: Boolean
    $isUseMember: Boolean
    $isUseShoppingCredit: Boolean
    $isRemoveGuessLike: Boolean
  ) {
    getShoppingCart(
      isRemoveMarkUp: $isRemoveMarkUp
      isUseMember: $isUseMember
      isUseShoppingCredit: $isUseShoppingCredit
      isRemoveGuessLike: $isRemoveGuessLike
    ) {
      shoppingTrolley {
        ...Order
      }
      outrightPurchase {
        ...Order
      }
    }

    activityCountdownByCart {
      activityCountdown {
        id
        createdAt
        activityId
        promotionId
        activityType
        name
        countdownTime
        priority
      }
      activityTag
    }
  }
  fragment Order on Order {
    ${shoppingCartOrderBaseFields}
    orderPickup {
      deliveryType
    }
  }
  ${shoppingCartOrderLineFields}
`;

/**
 * Step 2: CartInit - 购物车核心数据（2 合 1）
 * 不包含自提信息版本
 */
export const CART_INIT_WITHOUT_PICKUP_QUERY = gql`
  query CartInit(
    $isRemoveMarkUp: Boolean
    $isUseMember: Boolean
    $isUseShoppingCredit: Boolean
    $isRemoveGuessLike: Boolean
  ) {
    getShoppingCart(
      isRemoveMarkUp: $isRemoveMarkUp
      isUseMember: $isUseMember
      isUseShoppingCredit: $isUseShoppingCredit
      isRemoveGuessLike: $isRemoveGuessLike
    ) {
      shoppingTrolley {
        ...Order
      }
      outrightPurchase {
        ...Order
      }
    }

    activityCountdownByCart {
      activityCountdown {
        id
        createdAt
        activityId
        promotionId
        activityType
        name
        countdownTime
        priority
      }
      activityTag
    }
  }
  fragment Order on Order {
    ${shoppingCartOrderBaseFields}
  }
  ${shoppingCartOrderLineFields}
`;

/**
 * Step 3: CartExtras - 依赖 orderId 的附加数据（6 合 1）
 * 合并: orderPromotionResult + checkProductCanCheckout + freeGiftByOrder + markUpByOrder + getActiveCoupons + getOrderIsIncludeMemberPrice
 *
 * 需要从 Step 2 的 getShoppingCart 返回中提取 orderId 和 skuIds/skuQuantities 后请求
 */
export const CART_EXTRAS_QUERY = gql`
  query CartExtras(
    $orderIds: [ID!]!
    $orderId: ID!
    $skuIds: [ID!]!
    $skuQuantities: [SkuQuantityInput!]
  ) {
    # 订单促销优惠结果 - 与 fetchOrderPromotionResultDocument 保持一致
    orderPromotionResult(orderIds: $orderIds) {
      id
      createdAt
      updatedAt
      isCartShowActuallyPaidGift
      isCartShowFullDiscountPresentGift
      isCartShowSelectiveGift
      promResult {
        orderId
        discountAmount
        orderTotalPrice
        orderTotalPoints
        shoppingCreditsClaim
        shoppingCreditsDeduction
        disableShoppingCredits
        promLineResults {
          promInstanceId
          tags
          orderLines {
            orderLineId
            discountCount
            discountAmount
            skuId
            discount
            displayInThisGroup
            points
            cash
            wishBoxActivityBoxType
          }
          priority
          type
          shouldGroup
          description
          promTime
          promContent
          promOverview
          discountType
          discountAmount
          discountCount
          meetCondition
          superimposeType
          superimposeTypes
          orderLineBlindBoxMap {
            orderLineId
            blindBoxOrderBuyId
            blindBoxOrderBuyPrice
          }
          gifts {
            promInstanceId
            giftType
            items {
              giftId
              productId
              skuId
              count
              name
              giftPrice
              price
              selected
              isAvailable
              minimumStr
              ladderLevel
            }
          }
          coupons {
            couponId
            selected
            price
            autoSelected
          }
        }
        orderLinePromResults {
          orderLineId
          skuId
          count
          price
          discountAmount
          discountDetails {
            promInstanceId
            type
            superimposeType
            superimposeTypes
            discountCount
            discountAmount
          }
        }
        leftOrderLines {
          skuId
          lineId
          count
          price
          discountAmount
        }
        discountByTypes {
          type
          discountAmount
        }
        gifts {
          promInstanceId
          giftType
          items {
            giftId
            productId
            skuId
            count
            name
            giftPrice
            price
            selected
            isAvailable
            minimumStr
          }
        }
        coupons {
          couponId
          selected
          price
        }
        disableMember
      }
    }

    # 商品限购校验 - 与 fetchCheckProductCanCheckoutDocument 保持一致
    checkProductCanCheckout(skuIds: $skuIds, skuQuantities: $skuQuantities) {
      canCheckout
      reason
      errorDetail {
        type
        skuIds
        message
      }
    }

    # 赠品信息 - 与 fetchFreeGiftByOrderDocument 保持一致
    freeGiftByOrder(orderId: $orderId) {
      id
      name
      status
      product {
        id
        name
        enabled
        customFields {
          isVipProduct
          unit
        }
        optionGroups {
          id
          code
          name
          customFields {
            priority
          }
          options {
            id
            code
            name
            customFields {
              priority
            }
          }
        }
        variants {
          id
          name
          options {
            code
            name
            id
            group {
              id
              code
              name
            }
          }
          featuredAsset {
            source
          }
          isThereAnyStock
          price
          priceWithTax
          sku
        }
        featuredAsset {
          source
        }
        customFields {
          price
          markingPrice
        }
      }
    }

    # 加价购信息 - 使用 PURCHASE_PREMIUM_FRAGMENT
    markUpByOrder(orderId: $orderId) {
      ...PurchasePremium
    }

    # 优惠券列表 - 与 fetchGetActiveCouponsDocumenet 保持一致
    getActiveCoupons(orderId: $orderId) {
      availableCoupons {
        ...UserCoupon
      }
      couponsAreNotAvailable {
        ...UserCoupon
      }
      lackOfExchangeGoodsCoupons {
        ...UserCoupon
      }
    }

    # 订单是否包含会员价商品
    getOrderIsIncludeMemberPrice(orderId: $orderId)
  }
  ${PURCHASE_PREMIUM_FRAGMENT}
  ${USER_COUPON_FRAGMENT}
`;

